import {
  BarChart3,
  BookOpenCheck,
  BriefcaseBusiness,
  CircleDollarSign,
  FileText,
  Home,
  KeyRound,
  MessageSquare,
  CalendarClock,
  PanelsTopLeft,
  Plug,
  Settings,
  UserCog,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type PortalRole = "Owner" | "Admin" | "Agent" | "Client";

export type NavGroup = "Workspace" | "Operations" | "Organisation";

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Mirrors the `allowedRoles` on the matching route in `App.tsx`. */
  roles: PortalRole[];
  group: NavGroup;
  /** Extra path prefixes that keep this entry highlighted. */
  alsoMatches?: string[];
};

/**
 * The navigation model for all three roles.
 *
 * This list decides what is worth *showing*. Rust remains the authorization
 * boundary: it authorises every route and every read, and it will refuse a
 * request whether or not the browser drew a link to it.
 */
export const PORTAL_NAVIGATION: NavItem[] = [
  // Staff
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: BarChart3,
    roles: ["Owner", "Admin", "Agent"],
    group: "Workspace",
  },
  {
    to: "/projects",
    label: "Projects",
    icon: BriefcaseBusiness,
    roles: ["Owner", "Admin", "Agent"],
    group: "Workspace",
  },
  {
    to: "/messages",
    label: "Messages",
    icon: MessageSquare,
    roles: ["Owner", "Admin", "Agent"],
    group: "Workspace",
  },
  {
    to: "/operations",
    label: "Schedule & time",
    icon: CalendarClock,
    roles: ["Owner", "Admin", "Agent"],
    group: "Operations",
  },
  {
    to: "/learning",
    label: "Learning",
    icon: BookOpenCheck,
    roles: ["Owner", "Admin", "Agent"],
    group: "Operations",
  },
  // Client
  {
    to: "/home",
    label: "Home",
    icon: Home,
    roles: ["Client"],
    group: "Workspace",
  },
  {
    to: "/my-projects",
    label: "My projects",
    icon: BriefcaseBusiness,
    roles: ["Client"],
    group: "Workspace",
    alsoMatches: ["/project-journey"],
  },
  { to: "/properties", label: "Properties & viewings", icon: Home, roles: ["Owner", "Admin", "Agent", "Client"], group: "Workspace" },
  { to: "/project-updates", label: "Project updates", icon: FileText, roles: ["Owner", "Admin", "Agent", "Client"], group: "Workspace" },
  {
    to: "/documents",
    label: "Documents",
    icon: FileText,
    roles: ["Client"],
    group: "Workspace",
  },
  {
    to: "/budgeting",
    label: "Budgeting",
    icon: CircleDollarSign,
    roles: ["Client"],
    group: "Workspace",
  },
  {
    to: "/settings",
    label: "Workspace settings",
    icon: Settings,
    roles: ["Owner", "Admin"],
    group: "Organisation",
  },
  {
    to: "/user-management",
    label: "People & access",
    icon: UserCog,
    roles: ["Owner", "Admin"],
    group: "Organisation",
  },
  {
    to: "/task-list-manager",
    label: "Task templates",
    icon: PanelsTopLeft,
    roles: ["Owner", "Admin", "Agent"],
    group: "Operations",
  },
  {
    to: "/integrations",
    label: "Connections",
    icon: Plug,
    roles: ["Owner", "Admin"],
    group: "Organisation",
  },
];

export const NAV_GROUP_ORDER: NavGroup[] = [
  "Workspace",
  "Operations",
  "Organisation",
];

export type AccountItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: PortalRole[];
};

/** Account actions, which differ only in where each role edits its profile. */
export const ACCOUNT_NAVIGATION: AccountItem[] = [
  {
    to: "/myprofile",
    label: "Personal settings",
    icon: UserRound,
    roles: ["Owner", "Admin", "Agent"],
  },
  { to: "/profile", label: "Personal settings", icon: UserRound, roles: ["Client"] },
  {
    to: "/set-password",
    label: "Security",
    icon: KeyRound,
    roles: ["Owner", "Admin", "Agent", "Client"],
  },
];

/**
 * Resolves the role string the API returns. An unrecognised or missing role
 * resolves to `null`, which shows no navigation at all rather than guessing.
 */
export function normalizeRole(role?: string | null): PortalRole | null {
  const value = String(role || "").toLowerCase();
  if (value === "owner") return "Owner";
  if (value === "admin") return "Admin";
  if (value === "agent") return "Agent";
  if (value === "client") return "Client";
  return null;
}

export function navigationForRole(role?: string | null): NavItem[] {
  const normalized = normalizeRole(role);
  if (!normalized) return [];
  return PORTAL_NAVIGATION.filter((item) => item.roles.includes(normalized));
}

export function accountNavigationForRole(role?: string | null): AccountItem[] {
  const normalized = normalizeRole(role);
  if (!normalized) return [];
  return ACCOUNT_NAVIGATION.filter((item) => item.roles.includes(normalized));
}

/** Groups the entries for a role, dropping groups the role cannot reach. */
export function groupedNavigationForRole(
  role?: string | null,
): { group: NavGroup; items: NavItem[] }[] {
  const items = navigationForRole(role);
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    items: items.filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}

function pathMatches(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(`${target}/`);
}

/** Matches the entry's own path and anything below it, so a detail page keeps
 * its section highlighted. */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const current = String(pathname || "");
  return (
    pathMatches(current, item.to) ||
    (item.alsoMatches || []).some((extra) => pathMatches(current, extra))
  );
}

/**
 * How many placeholder rows the sidebar draws while the role is still loading.
 * Holding the tallest role's height keeps the shell from resizing when the
 * real entries arrive.
 */
export const NAVIGATION_PLACEHOLDER_COUNT = Math.max(
  ...(["Owner", "Admin", "Agent", "Client"] as PortalRole[]).map(
    (role) => navigationForRole(role).length,
  ),
);
